/**
 * Account record page.
 *
 * No prototype exists for this screen — `accounts.html` is the list only — so
 * the shell mirrors `LeadDetailPage.jsx`'s own structure (crumb, `.hl-panel`
 * identity header with its action row, then a stats/hierarchy sidebar plus a
 * tabbed centre column) rather than inventing a different convention for the
 * one detail page in the app that has no design reference.
 */
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  useAccount, useAccounts, useUpdateAccount, useDeleteAccount, useAccountOptions, useAccountContacts,
} from './useAccounts.js';
import { EmailComposerModal } from '../emailTemplates/EmailComposerModal.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { isMock } from '../../api/mockFallback.js';
import { PageLoader, Card, Avatar, ErrorState, EmptyState, MockBadge } from '../../components/common/index.jsx';
import { AccountFormModal, DeleteAccountModal, NotBuiltModal } from './AccountModals.jsx';
import {
  StatsCard, QuickFactsCard, AddressCard, HierarchyTab, ContactsTab, ActivitiesTab,
  OpportunitiesTab, DocumentsTab,
} from './AccountDetailViews.jsx';
import { toRow, healthTone, STAGE_PILL } from './accountShape.js';
import { number } from '../../utils/formatters.js';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'activities', label: 'Activities' },
  { key: 'hierarchy', label: 'Hierarchy' },
  { key: 'opportunities', label: 'Opportunities' },
  { key: 'documents', label: 'Documents' },
];

/**
 * Verbs the header offers that §5 documents but does not implement.
 *
 * `email` used to live here too — §8's email templates/send module is real
 * now (`modules/emailTemplates`), so the header button opens
 * `EmailComposerModal` instead of this notice. Only `merge` is still
 * genuinely unbuilt.
 */
const NOT_BUILT = {
  merge: {
    title: 'Merge is not available',
    why: 'Merging two account records needs the same field-winner machinery §4’s lead merge has, built for companies rather than people. Not implemented yet.',
    endpoint: 'POST /accounts/{id}/merge',
    section: 'docs/API.md §5, answers 501',
  },
};

export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();

  const { data: raw, isLoading, error, refetch } = useAccount(id);
  const options = useAccountOptions();
  const update = useUpdateAccount();
  const remove = useDeleteAccount();
  /* Just for the Edit modal's parent picker — a lightweight name-only lookup,
     not the record page's own account. */
  const { data: pickerData } = useAccounts({ limit: 100, sort: 'name' });
  const parentOptions = (pickerData?.data ?? []).map((a) => ({ value: a.id, label: a.name }));
  /* For the email composer's "send to this contact" picker. */
  const { data: contactsData } = useAccountContacts(id, { limit: 50 });
  const contacts = contactsData?.data ?? [];

  const [tab, setTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notBuilt, setNotBuilt] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  if (isLoading) return <PageLoader label="Loading account" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!raw) return <EmptyState icon="bi-question-circle" title="No such account" />;

  const account = toRow(raw);
  const canEdit = can('accounts', 'edit');
  const [bg, fg] = healthTone(account.healthBand);

  return (
    <>
      <div className="crumb mb-2">
        <Link to="/accounts">Accounts</Link><i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" />
        <span>{account.name}</span>
        <span className="ms-2 pill pill-gray">{account.account_no}</span>
        {isMock(raw) && <MockBadge section="accounts" />}
      </div>

      {/* ---- Highlights ---- */}
      <div className="hl-panel fade-up">
        <div className="d-flex align-items-start gap-3 flex-wrap">
          <div className="hl-icon"><i className="bi bi-building-fill" aria-hidden="true" /></div>
          <div className="min-w-0">
            <div className="eyebrow">Account</div>
            <h1 className="mb-0" style={{ fontSize: 'var(--fs-xl)', letterSpacing: '-.035em' }}>{account.name}</h1>
            <div className="t-xs ink-3 mt-1">
              <span>{account.industry ?? 'Industry not set'}{account.segment ? ` · ${account.segment}` : ''}</span>
              <span className={`pill ms-2 ${STAGE_PILL[account.lifecycleStage] ?? 'pill-gray'}`}>{account.lifecycleStage}</span>
              {account.healthBand && (
                <span className="pill ms-1" style={{ background: bg, color: fg, borderColor: 'transparent' }}>{account.healthBand}{account.healthScore != null ? ` · ${account.healthScore}` : ''}</span>
              )}
            </div>
          </div>
          <div className="ms-auto d-flex flex-wrap gap-2">
            {account.phone
              ? <a className="btn btn-soft btn-sm-x" href={`tel:${account.phone}`}><i className="bi bi-telephone me-1" aria-hidden="true" />Call</a>
              : <button type="button" className="btn btn-soft btn-sm-x" disabled><i className="bi bi-telephone me-1" aria-hidden="true" />Call</button>}
            <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setEmailOpen(true)}>
              <i className="bi bi-envelope me-1" aria-hidden="true" />Email
            </button>
            {canEdit && (
              <button type="button" className="btn btn-edit btn-sm-x" onClick={() => setEditOpen(true)}>
                <i className="bi bi-pencil me-1" aria-hidden="true" />Edit
              </button>
            )}
            <HeaderMenu
              onMerge={() => setNotBuilt(NOT_BUILT.merge)}
              onDelete={can('accounts', 'delete') ? () => setDeleting(true) : null}
            />
          </div>
        </div>

        <div className="hl-fields">
          <div><div className="k">Annual Revenue</div><div className="v">{account.revenue ? `$${Math.round(account.revenue).toLocaleString()}` : '—'}</div></div>
          <div><div className="k">Employees</div><div className="v">{account.employees != null ? number(account.employees) : '—'}</div></div>
          <div><div className="k text-truncate" style={{ maxWidth: 200 }}>Website</div><div className="v text-truncate" style={{ maxWidth: 200 }}>{account.website ?? '—'}</div></div>
          <div><div className="k">Territory</div><div className="v">{account.territory?.name ?? '—'}</div></div>
          <div>
            <div className="k">Owner</div>
            <div className="v d-flex align-items-center gap-1">
              {account.owner
                ? <><Avatar name={account.owner.full_name} size={22} />{account.owner.full_name}</>
                : <span className="pill pill-amber"><i className="bi bi-inbox" aria-hidden="true" />Unowned</span>}
            </div>
          </div>
          <div><div className="k">Customer Since</div><div className="v">{account.customerSince ?? '—'}</div></div>
        </div>
      </div>

      {/* ---- Body ---- */}
      <div className="row g-3 mt-1">
        <div className="col-xl-3 order-xl-1">
          <StatsCard account={account} />
          <QuickFactsCard account={account} />
          <AddressCard account={account} />
        </div>

        <div className="col-xl-9 order-xl-2">
          <Card bare className="fade-up">
            <div className="px-3 pt-3">
              <ul className="nav nav-tabs-x">
                {TABS.map((t) => (
                  <li key={t.key} className="nav-item">
                    <a
                      className={`nav-link ${tab === t.key ? 'active' : ''}`}
                      href="#" onClick={(e) => { e.preventDefault(); setTab(t.key); }}
                    >
                      {t.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-body-x">
              {tab === 'overview' && <OverviewTab account={account} />}
              {tab === 'contacts' && <ContactsTab account={account} />}
              {tab === 'activities' && <ActivitiesTab account={account} />}
              {tab === 'hierarchy' && <HierarchyTab account={account} onOpen={(nextId) => navigate(`/accounts/${nextId}`)} />}
              {tab === 'opportunities' && <OpportunitiesTab account={account} />}
              {tab === 'documents' && <DocumentsTab account={account} />}
            </div>
          </Card>
        </div>
      </div>

      <AccountFormModal
        open={editOpen}
        account={account}
        options={options}
        parentOptions={parentOptions}
        busy={update.isPending}
        onClose={() => setEditOpen(false)}
        onSubmit={async (patch) => {
          await update.mutateAsync({ id: account.id, patch, rowVersion: account.rowVersion });
          setEditOpen(false);
        }}
      />

      <DeleteAccountModal
        open={deleting}
        account={account}
        busy={remove.isPending}
        onClose={() => setDeleting(false)}
        onConfirm={async (reason) => {
          try {
            await remove.mutateAsync({ id: account.id, reason, rowVersion: account.rowVersion });
            setDeleting(false);
            navigate('/accounts');
          } catch {
            setDeleting(false);
          }
        }}
      />

      <NotBuiltModal open={Boolean(notBuilt)} action={notBuilt} onClose={() => setNotBuilt(null)} />

      <EmailComposerModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        anchor={{ account_id: account.id }}
        contacts={contacts}
      />
    </>
  );
}

function OverviewTab({ account }) {
  return (
    <div className="row g-3">
      {/* Full width, not col-md-6: three real columns need more room than a
          half-width card has — at col-md-6 the dt/dd pairs overlapped rather
          than fitting. Stacked instead of side-by-side. */}
      <div className="col-12">
        <div className="eyebrow mb-2">Company</div>
        <dl className="detail-grid detail-grid-3 mb-3">
          <div className="detail-row"><dt>Legal name</dt><dd>{account.legalName ?? '—'}</dd></div>
          <div className="detail-row"><dt>Industry</dt><dd>{account.industry ?? '—'}</dd></div>
          <div className="detail-row"><dt>Account type</dt><dd>{account.accountType ?? '—'}</dd></div>
          <div className="detail-row"><dt>Website</dt><dd>{account.website ?? '—'}</dd></div>
          <div className="detail-row"><dt>Email</dt><dd>{account.email ?? '—'}</dd></div>
          <div className="detail-row"><dt>Phone</dt><dd>{account.phone ?? '—'}</dd></div>
        </dl>
      </div>
      <div className="col-12">
        <div className="eyebrow mb-2">Lifecycle</div>
        <dl className="detail-grid detail-grid-3 mb-3">
          <div className="detail-row"><dt>Stage</dt><dd>{account.lifecycleStage}</dd></div>
          <div className="detail-row"><dt>Segment</dt><dd>{account.segment ?? '—'}</dd></div>
          <div className="detail-row"><dt>Customer since</dt><dd>{account.customerSince ?? '—'}</dd></div>
          <div className="detail-row"><dt>Churned on</dt><dd>{account.churnedOn ?? '—'}</dd></div>
          <div className="detail-row"><dt>Health</dt><dd>{account.healthBand ?? 'Not scored'}{account.healthScore != null ? ` (${account.healthScore})` : ''}</dd></div>
          <div className="detail-row"><dt>Parent account</dt><dd>{account.parent?.name ?? '—'}</dd></div>
        </dl>
      </div>
    </div>
  );
}

function HeaderMenu({ onMerge, onDelete }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const printNow = () => { if (typeof window !== 'undefined') window.print(); };

  const item = (icon, label, onClick, danger = false) => (
    <li>
      <button type="button" className={`dropdown-item ${danger ? 'danger' : ''}`} onClick={() => { setOpen(false); onClick(); }}>
        <i className={`bi ${icon} me-2`} aria-hidden="true" />{label}
      </button>
    </li>
  );

  return (
    <div className="dropdown" ref={box}>
      <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <i className="bi bi-caret-down-fill" style={{ fontSize: 10 }} aria-hidden="true" />
      </button>
      <ul className={`dropdown-menu dropdown-menu-end ${open ? 'show' : ''}`}>
        {item('bi-magic', 'Merge', onMerge)}
        {item('bi-printer', 'Print', printNow)}
        {onDelete && (
          <>
            <li><hr className="dropdown-divider" /></li>
            {item('bi-trash', 'Delete', onDelete, true)}
          </>
        )}
      </ul>
    </div>
  );
}
